import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { organisationActions } from '../slices/organisation.slice';
import {
  IOrganisationAccess,
  IOrganisationAccessRequestPayload,
  IOrganisationAccessUpdateRequest,
  IOrganisationControl,
  IOrganisationGetResponseOk,
  IOrganisationPayload,
  IOrganisationPutResponseOk,
  IOrganisationRemoveMemberPayload,
  IOrganisationTeamsResponse,
  TOrganisationGetAllPayload,
} from '../../types/@organisation';
import {
  fetchOrganisationAccessGet,
  fetchOrganisationAccessPut,
  fetchOrganisationGet,
  fetchOrganisationLeave,
  fetchOrganisationPut,
  fetchOrganisationRemoveMember,
  fetchOrganisationTeamsGet,
} from '../../services/organisation.service';
import { RootState } from '../store';
import { notificationActions } from '../slices/notification.slice';

function* organisationGetData({ payload }: PayloadAction<TOrganisationGetAllPayload>) {
  try {
    const fetchResult: IOrganisationGetResponseOk = yield call(fetchOrganisationGet, payload);
    yield put({ type: organisationActions['getOrganisations/ok'].type, payload: fetchResult });
  } catch (err) {
    yield put({ type: organisationActions['getOrganisations/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* organisationPutData({ payload }: PayloadAction<IOrganisationPayload>) {
  const contentForms: IOrganisationControl = yield select((state: RootState) => state.organisations.control);

  for (const key in contentForms) {
    payload.append(key, contentForms[key as keyof IOrganisationControl] || '');
  }

  try {
    const fetchResult: IOrganisationPutResponseOk = yield call(fetchOrganisationPut, payload);
    yield put({ type: organisationActions['putOrganisations/ok'].type, payload: fetchResult });
  } catch (err) {
    yield put({ type: organisationActions['putOrganisations/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* organisationAccessGetData({ payload }: PayloadAction<IOrganisationAccessRequestPayload>) {
  try {
    const fetchResult: IOrganisationAccess = yield call(fetchOrganisationAccessGet, payload.id);
    yield put({ type: organisationActions['getOrganisationAccess/ok'].type, payload: fetchResult });
  } catch (err) {
    yield put({ type: organisationActions['getOrganisationAccess/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* organisationTeamsGetData({ payload }: PayloadAction<IOrganisationAccessRequestPayload>) {
  try {
    const fetchResult: IOrganisationTeamsResponse = yield call(fetchOrganisationTeamsGet, payload.id);
    yield put({ type: organisationActions['getOrganisationTeams/ok'].type, payload: fetchResult });
  } catch (err) {
    yield put({ type: organisationActions['getOrganisationTeams/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* organisationAccessPutData({ payload }: PayloadAction<IOrganisationAccessUpdateRequest>) {
  try {
    const fetchResult: IOrganisationAccess = yield call(
      fetchOrganisationAccessPut,
      payload.id,
      payload.payload
    );
    yield put({ type: organisationActions['updateOrganisationAccess/ok'].type, payload: fetchResult });
    yield put({
      type: organisationActions.getOrganisationAccess.type,
      payload: { id: payload.id },
    });
  } catch (err) {
    yield put({ type: organisationActions['updateOrganisationAccess/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* organisationRemoveMemberData({ payload }: PayloadAction<IOrganisationRemoveMemberPayload>) {
  try {
    yield call(fetchOrganisationRemoveMember, payload.id, payload.userId);
    yield put({ type: organisationActions['removeOrganisationMember/ok'].type });
    yield put({
      type: organisationActions.getOrganisationAccess.type,
      payload: { id: payload.id },
    });
    yield put({
      type: organisationActions.getOrganisationTeams.type,
      payload: { id: payload.id },
    });
  } catch (err) {
    yield put({ type: organisationActions['removeOrganisationMember/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

function* organisationLeaveData({ payload }: PayloadAction<IOrganisationAccessRequestPayload>) {
  try {
    yield call(fetchOrganisationLeave, payload.id);
    yield put({ type: organisationActions['leaveOrganisation/ok'].type });
  } catch (err) {
    yield put({ type: organisationActions['leaveOrganisation/err'].type, payload: err });
    yield put({ type: notificationActions.error.type, payload: err });
  }
}

export function* watchOrganisation() {
  yield takeLatest(organisationActions.putOrganisations, organisationPutData);
  yield takeLatest(organisationActions.getOrganisations, organisationGetData);
  yield takeLatest(organisationActions.getOrganisationAccess, organisationAccessGetData);
  yield takeLatest(organisationActions.getOrganisationTeams, organisationTeamsGetData);
  yield takeLatest(organisationActions.updateOrganisationAccess, organisationAccessPutData);
  yield takeLatest(organisationActions.removeOrganisationMember, organisationRemoveMemberData);
  yield takeLatest(organisationActions.leaveOrganisation, organisationLeaveData);
}